/**
 * Service worker update coordination. Defers the reload triggered by a new
 * SW activation until no chat is streaming, so we never blow away in-flight
 * content. UpdateBanner surfaces the pending reload so Joseph can force it
 * early if he wants.
 */

import { signal } from '@preact/signals';
import { anyChatStreaming } from '../../modules/chat/chat-state';

/** True when a new SW has taken over and a reload is pending. */
export const updateReady = signal<boolean>(false);

let _pollTimer: ReturnType<typeof setTimeout> | null = null;
let _scheduled = false;
const IDLE_POLL_MS = 3000;

/**
 * Called from the SW `controllerchange` handler. Schedules a reload for the
 * next idle moment — i.e. when no chat is streaming. Idempotent against
 * rapid-fire controllerchange events (some browsers fire more than one).
 * Guard is a plain boolean set synchronously, not the signal value, since
 * two events in the same microtask could both observe `updateReady.value`
 * as false before either assignment lands.
 */
export function scheduleReloadOnIdle(): void {
  if (_scheduled) return;
  _scheduled = true;
  updateReady.value = true;

  const tick = () => {
    if (!anyChatStreaming()) {
      reloadNow();
      return;
    }
    _pollTimer = setTimeout(tick, IDLE_POLL_MS);
  };
  tick();
}

/** Force an immediate reload regardless of streaming state. */
export function reloadNow(): void {
  if (_pollTimer) {
    clearTimeout(_pollTimer);
    _pollTimer = null;
  }
  window.location.reload();
}
